import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraPageHeader, SpecraTooltip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { companySizeLabel } from '../../../Delivery/Registries/companySize';
import { registryHref } from '../../registryHref';
import { aliasLine, companyFamilyHref, companyRoles, openDesignation } from './companyIdentity';
import { sbaCertLabels } from './SbaProgramsSection';
import { useCompanySbaProfile } from './sbaClient';
import { UmbrellaPill } from './UmbrellaPill';
const kinds = {
    PARTNER: 'partner',
    COMPETITOR: 'competitor',
    TEAMMATE: 'teammate',
    PRIME_WE_SUB_TO: 'prime we sub to',
    OUR_SUB: 'our sub',
    PART_OF: 'part of',
    OURS: 'ours',
    MENTOR_TO: 'mentor to',
    PROTEGE_OF: 'protégé of'
};
export function companyRelationships(d) {
    return [
        ...d.relationships
    ].sort((a, b)=>Number(!!a.until) - Number(!!b.until)).map((r)=>`${r.until ? 'former ' : ''}${kinds[r.kind] ?? r.kind.toLowerCase().replace(/_/g, ' ')}`).filter((v, i, all)=>all.indexOf(v) === i);
}
export function CompanyHeader({ dossier: d, actions }) {
    const sba = useCompanySbaProfile(d.companyId);
    const certs = sbaCertLabels(sba.data?.certifications ?? []);
    const relationships = companyRelationships(d);
    const size = [
        d.businessSize && d.businessSize !== 'UNKNOWN' ? companySizeLabel(d.businessSize) : '',
        d.samBusinessSize && d.samBusinessSize !== 'UNKNOWN' ? `SAM: ${companySizeLabel(d.samBusinessSize).toLowerCase()}` : ''
    ].filter(Boolean).join(' · ');
    const vehicles = d.vehicleHoldings.length ? d.vehicleHoldings.flatMap((v)=>v.vehicle ? [
            v.vehicle
        ] : []) : d.priceListVehicles;
    const acquired = d.lineage.filter((l)=>l.relationshipType === 'acquisition' && l.direction === 'TO_US');
    const alsoKnownAs = aliasLine(d.aliases);
    const roles = companyRoles(d.relationships);
    const partner = openDesignation(d.designations);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "home",
                title: d.name,
                eyebrow: `Company${relationships.length ? ` · ${relationships.slice(0, 2).join(' and ')}` : ''}`,
                description: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        size,
                        vehicles.map((v, i)=>/*#__PURE__*/ _jsxs("span", {
                                children: [
                                    (size || i > 0) && ' · ',
                                    !d.vehicleHoldings.length && 'price list on ',
                                    /*#__PURE__*/ _jsx(SpecraLink, {
                                        href: registryHref(v) ?? undefined,
                                        renderLink: renderRouterLink,
                                        children: v.name
                                    })
                                ]
                            }, v.id)),
                        acquired.map((l)=>/*#__PURE__*/ _jsxs("span", {
                                children: [
                                    ' ',
                                    "· acquired ",
                                    l.other?.name,
                                    l.effectiveStart && ` in ${l.effectiveStart.toDate().getUTCFullYear()}`,
                                    " (history kept under both names)"
                                ]
                            }, l.relationshipId)),
                        certs.length > 0 && ` · ${certs.join(' · ')}`
                    ]
                }),
                actions: actions
            }),
            alsoKnownAs && /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                tone: "muted",
                "data-testid": Testids.registryCompany.aliasLine,
                children: [
                    "Also known as: ",
                    alsoKnownAs
                ]
            }),
            (roles.current.length > 0 || d.umbrella) && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(UmbrellaPill, {
                        companyName: d.name,
                        umbrella: d.umbrella,
                        familyHref: companyFamilyHref(d.companyId)
                    }),
                    roles.current.map((r)=>r.kind === 'PARTNER' && partner ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: partnerTip(partner),
                            children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "success",
                                "data-testid": Testids.registryCompany.partnerChip,
                                children: r.label
                            })
                        }, r.label) : /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "neutral",
                            "data-testid": Testids.registryCompany.roleChip,
                            children: r.label
                        }, r.label))
                ]
            }),
            roles.former.length > 0 && /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                tone: "muted",
                children: [
                    "Former: ",
                    roles.former.map((r)=>r.qualifier || r.label).join(' · ')
                ]
            })
        ]
    });
}
function partnerTip(p) {
    return [
        p.notes,
        p.sourceArtifactId ? 'Source document attached.' : ''
    ].filter(Boolean).join(' ') || 'Marked as a partner by a registry editor.';
}
